// Brand colors and shared logo components
import { Linkedin, Download } from "lucide-react";
import { useState } from "react";
import { Link, useLocation } from "react-router-dom";

// The Multiplier Brand Colors
export const BRAND = {
  gold: "#D4AF37",
  goldLight: "#E8C547",
  goldDark: "#B8960F",
  charcoal: "#1C1C1C",
  charcoalLight: "#2A2A2A",
  surface: "#0A0A0A",
  text: "#FAFAFA",
  textMuted: "#A1A1AA",
  accent: "#FF5233"
};

export const PROFILE = {
  linkedin: "https://www.linkedin.com/in/dan-leitner-b6a60723/",
  email: "dleit1229@gmail.com",
  resume: "/Dan-Leitner-Resume.pdf",
  photo: "https://customer-assets-0z36b82j.emergentagent.net/job_efficiency-boost-10/artifacts/7aae5664c5099e25_image.png"
};

// Multiplier Logo Icon - Pure ascending bars
export const MultiplierIcon = ({ size = 40, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 64 64" fill="none" className={className}>
    <rect x="6" y="46" width="11" height="14" fill={BRAND.gold} opacity="0.5" rx="2"/>
    <rect x="20" y="36" width="11" height="24" fill={BRAND.gold} opacity="0.65" rx="2"/>
    <rect x="34" y="24" width="11" height="36" fill={BRAND.gold} opacity="0.8" rx="2"/>
    <rect x="48" y="10" width="11" height="50" fill={BRAND.gold} rx="2"/>
  </svg>
);

// Personal "DL" monogram
export const DLMark = ({ size = 36 }) => (
  <div
    className="flex items-center justify-center rounded-lg border-2 border-[#D4AF37] text-[#D4AF37] font-bold tracking-tight flex-shrink-0"
    style={{ width: size, height: size, fontSize: size * 0.42 }}
    data-testid="dl-mark"
  >
    DL
  </div>
);

// Logo with wordmark - clickable to go home
export const Logo = () => (
  <Link to="/" className="flex items-center gap-3 hover:opacity-90 transition-opacity" data-testid="header-logo">
    <DLMark size={36} />
    <div className="flex flex-col">
      <span className="text-lg font-bold tracking-tight text-white leading-none">Dan Leitner</span>
      <span className="text-[10px] text-zinc-400 tracking-widest uppercase">Leitner Revenue Systems</span>
    </div>
  </Link>
);

// Navigation
export const Navigation = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  
  // For homepage, use hash anchors. For other pages, link to homepage with hash
  const getNavHref = (hash) => {
    if (isHomePage) {
      return hash; // Just the hash for same-page scrolling
    }
    return `/${hash}`; // Full path for cross-page navigation
  };
  
  const navItems = [
    { label: "About", href: "#about" },
    { label: "Experience", href: "#experience" },
    { label: "Insights", href: "/insights", isPage: true },
    { label: "Resources", href: "/resources", isPage: true },
    { label: "LRS", href: "#lrs" },
    { label: "Contact", href: "#contact" }
  ];
  
  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between h-20">
          <Logo />
          
          <nav className="hidden md:flex items-center gap-8">
            {navItems.map((item) => (
              item.isExternal ? (
                <a 
                  key={item.label}
                  href={item.href}
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              ) : item.isPage ? (
                <Link 
                  key={item.label}
                  to={item.href}
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </Link>
              ) : (
                <a 
                  key={item.label}
                  href={getNavHref(item.href)}
                  className="text-sm text-zinc-400 hover:text-white transition-colors"
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  {item.label}
                </a>
              )
            ))}
          </nav>

          <div className="flex items-center gap-4">
            <a 
              href={PROFILE.linkedin} 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-zinc-400 hover:text-[#D4AF37] transition-colors"
              data-testid="linkedin-link"
            >
              <Linkedin className="w-5 h-5" />
            </a>
            <a 
              href={PROFILE.resume}
              download
              className="hidden md:inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4AF37] text-[#0A0A0A] rounded-full text-sm font-semibold hover:bg-[#E8C547] transition-all"
              data-testid="cta-header"
            >
              Resume
              <Download className="w-4 h-4" />
            </a>
            
            {/* Mobile menu button */}
            <button 
              className="md:hidden text-white"
              onClick={() => setMobileOpen(!mobileOpen)}
              data-testid="mobile-menu-btn"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                {mobileOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile menu */}
        {mobileOpen && (
          <div className="md:hidden py-4 border-t border-white/5">
            {navItems.map((item) => (
              item.isExternal ? (
                <a 
                  key={item.label}
                  href={item.href}
                  className="block py-3 text-zinc-400 hover:text-white transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </a>
              ) : item.isPage ? (
                <Link 
                  key={item.label}
                  to={item.href}
                  className="block py-3 text-zinc-400 hover:text-white transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </Link>
              ) : (
                <a 
                  key={item.label}
                  href={getNavHref(item.href)}
                  className="block py-3 text-zinc-400 hover:text-white transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  {item.label}
                </a>
              )
            ))}
          </div>
        )}
      </div>
    </header>
  );
};
